import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Receipt, CheckCircle, Warning, DownloadSimple, ArrowSquareOut } from "@phosphor-icons/react";

/**
 * AccountingPanel — per-tenant accounting provider config + invoice export.
 * Props: tenant, invoices, onConfigSaved(updatedTenant)
 */
export default function AccountingPanel({ tenant, invoices = [], onConfigSaved }) {
  const [provider, setProvider] = useState(tenant?.accounting_provider || "");
  const [apiKey, setApiKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const keySet = !!tenant?.accounting_api_key_set;

  useEffect(() => {
    setProvider(tenant?.accounting_provider || "");
    setApiKey("");
  }, [tenant]);

  const save = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await api.patch(`/tenants/${tenant.tenant_id}/accounting`, {
        provider: provider || null,
        api_key: provider === "datev" ? "" : (apiKey || (keySet ? undefined : "")),
      });
      onConfigSaved?.(r.data);
      setMsg("Konfiguration gespeichert");
      setApiKey("");
    } catch (e) { setErr(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  };

  const exportInvoice = async (invoiceId) => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      if (provider === "datev") {
        // Download CSV
        const r = await api.post(`/invoices/${invoiceId}/export-accounting`, {}, { responseType: "blob" });
        const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
        const a = document.createElement("a");
        a.href = url; a.download = `DATEV_${invoiceId}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        setMsg("DATEV-CSV heruntergeladen");
      } else {
        const r = await api.post(`/invoices/${invoiceId}/export-accounting`);
        setMsg(`An ${provider} übertragen — ID: ${r.data?.external_id || "OK"}`);
      }
    } catch (e) {
      const d = e.response?.data;
      setErr(typeof d === "string" ? d : (d?.detail || e.message));
    } finally { setBusy(false); }
  };

  const PROVIDERS = {
    lexoffice: { name: "Lexware Office (Lexoffice)", help: "API-Key aus app.lexware.de → Add-ons → Public API" },
    sevdesk:   { name: "sevDesk", help: "32-stelligen API-Token aus sevDesk → Einstellungen → Benutzer kopieren" },
    datev:     { name: "DATEV (CSV-Export)", help: "Erzeugt einen DATEV-Buchungsstapel als CSV-Download — kein API-Key nötig" },
  };

  return (
    <div className="bg-white rounded-2xl border border-black/5 p-6 max-w-3xl">
      <div className="flex items-center gap-2 mb-1">
        <Receipt size={20} weight="bold" style={{ color: "var(--tonneva-primary)" }} />
        <h3 className="font-heading text-xl font-bold tracking-tight">Buchhaltung</h3>
      </div>
      <p className="text-sm text-zinc-500 mb-5">
        Übergeben Sie Rechnungen automatisch an Ihr Buchhaltungssystem — Lexoffice, sevDesk oder als DATEV-Export.
      </p>

      <div className="grid sm:grid-cols-3 gap-3 mb-3">
        <div>
          <Label className="text-xs">Anbieter</Label>
          <Select value={provider} onValueChange={setProvider}>
            <SelectTrigger className="rounded-md h-11" data-testid="accounting-provider"><SelectValue placeholder="Auswählen…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="lexoffice">Lexware Office</SelectItem>
              <SelectItem value="sevdesk">sevDesk</SelectItem>
              <SelectItem value="datev">DATEV (CSV)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {provider && provider !== "datev" && (
          <div className="sm:col-span-2">
            <Label className="text-xs">API-Key {keySet && tenant.accounting_provider === provider && <span className="text-emerald-600 ml-1">✓ gespeichert</span>}</Label>
            <Input
              type="password"
              className="h-11 rounded-md font-mono"
              placeholder={keySet && tenant.accounting_provider === provider ? "•••••••• (überschreiben)" : "API-Key einfügen"}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              data-testid="accounting-api-key"
            />
          </div>
        )}
      </div>

      {provider && PROVIDERS[provider] && (
        <p className="text-xs text-zinc-500 mb-3" data-testid="accounting-help">{PROVIDERS[provider].help}</p>
      )}

      <div className="flex gap-2">
        <Button onClick={save} disabled={busy} className="rounded-md text-white" style={{ background: "var(--tonneva-gradient)" }} data-testid="accounting-save">
          {busy ? "…" : "Konfiguration speichern"}
        </Button>
      </div>

      {err && <div className="text-sm text-red-600 bg-red-50 rounded-md p-2.5 mt-3" data-testid="accounting-error">{err}</div>}
      {msg && <div className="text-sm text-emerald-700 bg-emerald-50 rounded-md p-2.5 mt-3" data-testid="accounting-msg">{msg}</div>}

      {tenant?.accounting_provider && (
        <div className="mt-6 pt-6 border-t border-black/5">
          <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
            <CheckCircle size={16} weight="bold" style={{ color: "#16A34A" }} />
            Rechnungen exportieren ({invoices.length})
          </h4>
          {invoices.length === 0 ? (
            <p className="text-sm text-zinc-400">Noch keine Rechnungen vorhanden.</p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {invoices.map((i) => (
                <div key={i.invoice_id} className="flex items-center justify-between p-3 border border-black/10 rounded-md text-sm" data-testid={`accounting-row-${i.invoice_id}`}>
                  <div>
                    <div className="font-mono font-semibold">{i.number}</div>
                    <div className="text-xs text-zinc-500">€{(i.total_cents/100).toFixed(2)} · {new Date(i.created_at).toLocaleDateString("de-DE")}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {i.accounting_exported_at && (
                      <span className="text-[10px] uppercase tracking-widest text-emerald-600 font-bold flex items-center gap-1">
                        <CheckCircle size={12} weight="bold" /> exportiert
                      </span>
                    )}
                    <Button size="sm" variant="outline" className="rounded-md h-8 text-xs" onClick={() => exportInvoice(i.invoice_id)} disabled={busy} data-testid={`accounting-export-${i.invoice_id}`}>
                      {tenant.accounting_provider === "datev" ? <DownloadSimple size={12} weight="bold" className="mr-1" /> : <ArrowSquareOut size={12} weight="bold" className="mr-1" />}
                      {tenant.accounting_provider === "datev" ? "CSV" : "Export"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
